import {
  ApartmentOutlined,
  LineChartOutlined,
  LoadingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Breadcrumb,
  Card,
  Empty,
  PageHeader,
  Statistic,
  Tabs,
  Typography,
  message
} from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SurveyCampaignStatusTag from "../../components/SurveyCampaignStatusTag";
import {
  GetSurveyCampaignBasicInfoRequest,
  GetSurveyCampaignBasicInfoResponse,
} from "../../constants/apiRequestResponse";
import { SurveyCampaign, SurveyEmployee, SurveyTemplateType } from "../../constants/types";
import { getSurveyCampaignBasicInfo } from "../../services/api";
import { ReportViewPageKeys } from "../../services/i8tn/ReportViewPage/keys";
import { getQueryParam, parseDate, parseTimeAgo, setQueryParam } from "../../util/index";
import FeedbackGroupView from "./FeedbackGroupView";
import FeedbackIndividualView from "./FeedbackIndividualView";
import FeedbackTeamView from "./FeedbackTeamView";
import DefaultGroupView from "./DefaultGroupView";
import styles from "./ReportViewPage.module.css";
import { COLORS } from "../../constants/misc";

export enum Modes {
  groupReport = "groupReport",
  individualReport = "individualReport",
  teamReport = "teamReport",
}

export type ChooseOrView = "choose" | "view"
export type CurrentOrCompare = "current" | "compare"

// Derive comparison UI colors from design system
export const COMPARISON_COLORS = {
  past: {
    primary: COLORS.PRIMARY_COLOR,           // #c01172 - accent for selection
    border: "#e8a3c4",                        // lighter variant of primary
    background: "#fdf2f7",                    // very light pink
  },
  present: {
    primary: "#595959",                       // neutral dark grey
    border: "#d9d9d9",                        // standard border grey
    background: "#fafafa",                    // subtle grey background
  },
};

export function ComparisonSlot(props: {
  pastOrPresent: "past" | "present",
  surveyCampaign?: SurveyCampaign,
  showClear?: boolean,
  onClear?: () => void,
  employee?: SurveyEmployee,
  team?: string
}) {
  const { t } = useTranslation();
  const colors = COMPARISON_COLORS[props.pastOrPresent];
  const showAsSelected = props.pastOrPresent === "past" && !!props.surveyCampaign;
  const employeeName = props.employee && `${props.employee?.fname} ${props.employee?.lname}`

  return (
    <div style={{
      border: showAsSelected ? `1px solid ${colors.border}` : "1px solid #d9d9d9",
      borderRadius: 8,
      padding: 15,
      minHeight: 120,
      background: showAsSelected ? colors.background : "#fafafa",
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: showAsSelected ? colors.primary : "#8c8c8c", marginBottom: 4 }}>
        {props.pastOrPresent.toUpperCase()}
      </div>
      {props.surveyCampaign ? (
        <>
          {
            employeeName && (
              <div style={{ fontSize: 14 }}>
                {employeeName}
              </div>
            )
          }
          {
            props.team && (
              <div style={{ fontSize: 14 }}>
                {props.team}
              </div>
            )
          }
          <div style={{ fontSize: 14 }}>
            {props.surveyCampaign.name}
          </div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>
            {parseDate(props.surveyCampaign.createdAt)}
            {props.showClear && props.onClear && (
              <span
                style={{ marginLeft: 8, color: colors.primary, cursor: "pointer", textDecoration: "underline" }}
                onClick={props.onClear}
              >
                {t(ReportViewPageKeys.reportsView_clearButton)}
              </span>
            )}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 14, color: "#bfbfbf" }}>{t(ReportViewPageKeys.reportsView_selectFromListBelow)}</div>
      )}
    </div>
  );
}

export default function ReportsViewPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [report, setReport] = useState<
    GetSurveyCampaignBasicInfoResponse | undefined
  >();

  const surveyCampaignId = getQueryParam("surveyCampaignId") || "";

  const completionRate = getCompletionRate();
  const { t } = useTranslation();

  function getCompletionRate() {
    if (!report) return 0;
    if (!report.numberOfCompleted) return 0;
    if (!report.numberOfResponse) return 0;
    return Number(
      ((report.numberOfCompleted / report.numberOfResponse) * 100).toFixed(0)
    );
  }

  useEffect(() => {
    setIsLoading(true);

    const req: GetSurveyCampaignBasicInfoRequest = {
      surveyCampaignId: surveyCampaignId,
    };
    getSurveyCampaignBasicInfo(req)
      .then((res) => {
        if (!res.success) throw new Error(res.message);

        if (res.surveyCampaign?.name) {
          document.title = res.surveyCampaign?.name
        }
        setReport(res);
      })
      .catch((err) => {
        message.error(err.message);
      })
      .finally(() => setIsLoading(false));
  }, [surveyCampaignId]);

  if (!surveyCampaignId) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        <Typography.Title level={5}>{t(ReportViewPageKeys.reportsView_invalidSurveyCampaign)}</Typography.Title>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingOutlined style={{ fontSize: 50 }} />;
  }

  return (
    <div className="page-content">
      <Breadcrumb>
        <Breadcrumb.Item>
          <a href={`/report`}>
            <>{t(ReportViewPageKeys.reportsView_reportsBreadcrumb)}</>
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <a href={`/report?surveyCampaignId=${surveyCampaignId}`}>
            {`${report?.surveyCampaign?.name}`}
          </a>
        </Breadcrumb.Item>
      </Breadcrumb>
      <PageHeader
        title={
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            <span>{`${report?.surveyCampaign?.name}`}</span>
            <SurveyCampaignStatusTag status={report?.surveyCampaign?.status} />
          </div>
        }
        subTitle={`${t(
          ReportViewPageKeys.reportsView_lastUpdatedString
        )} ${parseTimeAgo(report?.surveyCampaign?.updatedAt)}`}
        onBack={() => {
          window.location.href = '/report'
        }}
      />
      <div
        style={{
          display: "flex",
          columnGap: 20,
          rowGap: 20,
          flexDirection: "column",
        }}
      >
        <div className={styles.card_container} style={{ flex: 1 }}>
          <Card className={styles.card}>
            <Statistic
              title={t(ReportViewPageKeys.reportsView_totalString)}
              value={report?.numberOfResponse}
              precision={0}
            />
          </Card>
          <Card className={styles.card}>
            <Statistic
              title={t(ReportViewPageKeys.reportsView_completedString)}
              value={report?.numberOfCompleted}
              precision={0}
            />
          </Card>
          <Card className={styles.card}>
            <Statistic
              title={t(ReportViewPageKeys.reportsView_cancelledString)}
              value={report?.numberOfCancelled}
              precision={0}
            />
          </Card>

          <Card className={styles.card}>
            <Statistic
              title={t(ReportViewPageKeys.reportsView_completionRateString)}
              value={`${completionRate} %`}
              precision={0}
            />
          </Card>
        </div>
        {report?.surveyTemplate?.type === SurveyTemplateType.feedback ? (
          // 360 view
          <Tabs
            style={{
              display: "flex",
              width: "100%",
              flex: 5,
            }}
            defaultActiveKey={getQueryParam("layout") || ""}
            onChange={(key) => {
              setQueryParam("layout", key);
            }}
          >
            <Tabs.TabPane
              tab={
                <p>
                  <LineChartOutlined />{" "}
                  {t(ReportViewPageKeys.reportsView_groupReportTab)}
                </p>
              }
              key={Modes.groupReport}
            >
              <FeedbackGroupView report={report} />
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={
                <p>
                  <TeamOutlined />
                  {t(ReportViewPageKeys.reportsView_teamReportTab)}
                </p>
              }
              key={Modes.teamReport}
            >
              <FeedbackTeamView report={report} />
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={
                <p>
                  <ApartmentOutlined />
                  {t(ReportViewPageKeys.reportsView_individualsReportTab)}
                </p>
              }
              key={Modes.individualReport}
            >
              <FeedbackIndividualView report={report} />
            </Tabs.TabPane>
          </Tabs>
        ) : (
            <DefaultGroupView
            report={report}
            surveyCampaignId={surveyCampaignId}
            />
        )}
      </div>
    </div>
  );
}
